import { getLocalStatus, getRemoteStatus, Status, nowStatus } from '@model/show-activity-room';
import { Toast } from '@tencent/calendar-web-component-mobile';
import { message } from '@tencent/tea-component';
import { AppContext } from '@utils/browser/app-context';
import IS_DEV from '@utils/env/is-dev';
import {
  checkLogin,
  getUserInfoFromSession,
  goLogin,
  UserData,
  UserDataObject,
} from '@utils/login-util';
import { isMobile } from '@utils/ua/is-mobile';
import { useRouter } from 'next/router';
import React, { useEffect, useState, FC, ReactElement, useMemo, useRef } from 'react';
import ToastIcon from 'src/components/ToastIcon';
import useViewPoint from 'src/hooks/useViewPoint';
import {
  getHostDetail,
  HostDetailData,
  HostList,
  getUserHostList,
  setUserHostId,
} from 'src/model/host-account';
import {
  NO_NEED_LOGIN_PAGES,
  HOST_ADMIN,
  NO_NEED_SHOW_AXTIVITY_ROOM,
  VIEW_IN_FIRST_PAGE,
} from './auth-page-config';
import beforeLogic from 'src/logic/beforeRouter';
import debounce from 'lodash/debounce';
import { useResourceDetail } from '@hooks/useCommercialize';

interface IProps {
  children: ReactElement;
  isMobile?: boolean;
  userData: UserDataObject;
}

interface ContextWarpProps {
  children: ReactElement;
  userInfo: Partial<UserData>;
  isMobile?: boolean;
}

const RouterBefore: FC<IProps> = ({ children, isMobile: ism, userData }) => {
  const router = useRouter();
  const { pathname } = router;
  const firstShow = useMemo(() => VIEW_IN_FIRST_PAGE.includes(pathname), []);
  const [show, setShow] = useState(firstShow);

  useEffect(() => {
    // 根据来源禁用掉导航图标. 由于页面可能有 location.href 跳转, 且参数不一定都带着，
    // 由于页面入口处有登录跳转等, query 会丢失, 所以放在 router-before 处理
    const disable = router.query._from === 'inmeeting';
    // 仅一次更新 disable, 直到下次访问/刷新页面
    if (disable) {
      sessionStorage.navDialogDisable = true;
    }
  }, []);

  useEffect(() => {
    if (IS_DEV && localStorage.getItem('__DEBUG__')) {
      setShow(true);
      return;
    }
    const { pathname, asPath, query } = router;
    beforeLogic.invoke('pageAccess', {
      pathname,
      asPath,
      query,
      NO_NEED_LOGIN_PAGES,
      HOST_ADMIN,
      NO_NEED_SHOW_AXTIVITY_ROOM,
      checkLogin,
      getLocalStatus,
    });
  }, [router]);
  useEffect(() => {
    beforeLogic.on('beforePageShow', () => {
      setShow(true);
    });

    beforeLogic.on('routerPage', (path: string) => {
      if (path === 'login') {
        goLogin();
        return;
      }
      let pathname = `/${path}`;
      // 只有可能跳转到广场
      if (path.indexOf('square') < 0) {
        pathname = `${process.env.basePath}/${path}`;
      }
      routerPage(pathname);
    });
    // 当前传入参数为转译的页面链接
    // 保留进入页面时的链接
    beforeLogic.on('replacePage', (path: string) => {
      if (!path) {
        return;
      }
      window.location.replace(decodeURIComponent(path));
    });
    beforeLogic.on('checkLoginErr', (e) => {
      console.error('checkLogin', e);
      message.warning({
        content: '后台服务出错，请刷新页面重试',
      });
    });
  }, []);

  const routerPage = (path: string) => {
    const url = `${window.location.origin}${path}`;
    window.location.replace(url); // 这里为啥用router会是空白页？ todo
  };
  return show ? (
    <ContextWrap userInfo={userData.userInfo} isMobile={ism}>
      {children}
    </ContextWrap>
  ) : null;
};

const ContextWrap: FC<ContextWarpProps> = ({ children, userInfo, isMobile: ism }) => {
  const [mobile, setIsMobile] = useState<boolean | undefined>(ism);
  const [hostDetail, setHostDetail] = useState<Partial<HostDetailData>>({});
  const [userData, setUserData] = useState<Partial<UserData>>(userInfo);
  const [isHost, setIsHost] = useState<boolean>();
  const [regiHostData, setRegiHostData] = useState<Partial<HostDetailData>>({});
  const router = useRouter();
  const { query, pathname } = router;
  const { isMobileUi } = useViewPoint();
  const [userHostList, setUserHostList] = useState<any>([]);
  const [selectHostId, setSelectedHostId] = useState<string | number | undefined>('');
  const vipResourceDetail = useResourceDetail();
  const [statusInfo, setStatusInfo] = useState<Status>(nowStatus);
  /** 为了处理组件渲染导致重复请求batch */
  const firstRef = useRef<any>();
  const getOrSetLogin = () => {
    const info = getUserInfoFromSession();
    setUserData(info || {});
    if (!info?.app_id) {
      // 还没登录再刷一遍
      checkLogin().then(() => {
        setUserData(getUserInfoFromSession() || {});
      });
    }
  };
  useEffect(() => {
    // 当前用户有登录态时获取用户权限，不根据页面限制
    if (!!userData?.userid && !firstRef.current) {
      firstRef.current = 1;
      getLocalStatus()
        .then((status: any) => {
          setStatusInfo(status);
        })
        .catch(() => {
          firstRef.current = 0;
        });
    }
  }, [userData?.userid]);

  useEffect(() => {
    setIsMobile(isMobile() || window.innerWidth < 480);
    const showRegetLoginDate = (e: any) => {
      if (e.persisted) {
        getOrSetLogin();
      }
    };
    window.addEventListener('pageshow', showRegetLoginDate);
    getOrSetLogin();
    return () => {
      window.removeEventListener('pageshow', showRegetLoginDate);
    };
  }, []);
  useEffect(() => {
    if (statusInfo?.is_host !== undefined) {
      setIsHost(statusInfo.is_host || statusInfo.is_host_org_member);
    }
  }, [statusInfo]);
  // 主办方更改后同步更新主办方商业化权限信息
  useEffect(() => {
    if (!isHost) {
      return;
    }
    const hostId = String(hostDetail.host_id || '');
    vipResourceDetail.updateVipResource(hostId);
  }, [hostDetail.host_id, isHost]);

  useEffect(() => {
    /** todo 修复除主办方页面外使用appContent的hostDetail数据后再限制非主办方管理页面调用该接口 */
    // const isHostPage = HOST_ADMIN.indexOf(pathname) !== -1;
    if (isHost) {
      updateHostList();
    }
  }, [isHost]);
  useEffect(() => {
    if (!userData.app_id || hostDetail.host_id) {
      return;
    }
    if (userData.app_id) {
      window.nextAegis?.setConfig({
        // @ts-ignore
        ext3: BUILD_TIME || '0.0.1',
      });
    }
  }, [userData, isHost]);

  const isPath = (path: string) => {
    const { pathname } = window.location;
    return pathname && pathname.indexOf(path) !== -1;
  };

  function updateAppCtxHostDetail(id?: string | number, detail?: any) {
    const selectId = (id as string) || (selectHostId as string) || '';
    const extra = detail || getHostRoleInList();
    return getHostDetail(selectId)
      .then((res) => {
        setHostDetail({
          ...res,
          ...extra,
        });
        sessionStorage.setItem('user:hostId', String(res?.host_id));
        if (isPath('/home') && query?.host_id !== res?.host_id) {
          router.replace({
            pathname: '/home',
            query: Object.assign(query, { host_id: res?.host_id }),
          });
        }
      })
      .catch((e) => {
        console.log(`获取主办方信息失败${e}`);
        if (!e?.code) {
          return;
        }
        message.warning({
          content: e?.msg,
        });
        // 当前用户无权限
        if (e?.code === 1010176) {
          router.push('/news');
        }
      });
  }

  const updateRegisterHostData = (registerHostData: any) => {
    setRegiHostData(registerHostData);
  };
  // 获取主办方权限
  const getHostRoleInList = (selectId?: string, list?: HostList) => {
    const data = list || userHostList;
    const id = selectId || selectHostId;
    if (!id || data?.length <= 0) {
      return;
    }
    console.log('getHostRoleInList', data, id);
    const detail = data.find((element: any) => String(element?.host_id) === id) || {};
    return detail;
  };

  // 设置选中主办方id
  const setSelectHostId = (id?: string, callBack?: Function) => {
    if (!id || id === hostDetail?.host_id) {
      callBack?.('');
      return;
    }
    setSelectedHostId(id);
    setUserHostId(id).then((res) => {
      if (res === id && id !== hostDetail?.host_id) {
        const detail = getHostRoleInList(id);
        updateAppCtxHostDetail(id, detail);
        console.log('updateAppCtxHostDetail 11', id);
        callBack?.(id);
        return;
      }
      if (Number(res) === -1) {
        setSelectedHostId(hostDetail?.host_id as string);
        updateHostList();
        callBack?.(hostDetail?.host_id);
      }
    });
  };

  // 获取用户主办方列表
  const updateHostList = debounce(async (callBack?: Function) => {
    const res = await getUserHostList();
    setUserHostList(res?.org_list);
    let selectId = res?.selected_host_id;
    // 当前主办方列表不含主办方
    if (res?.org_list?.length === 0) {
      setSelectedHostId(selectId);
      const isHostPage = HOST_ADMIN.indexOf(pathname) !== -1;
      if (isHostPage) {
        const data = await getRemoteStatus();
        setIsHost(data?.is_host || data?.is_host_org_member);
        getOrSetLogin();
        let routerPath = '/no-permission';
        if (data?.is_beta_user && !data?.is_host) {
          routerPath = '/create';
        }
        router.replace(routerPath);
      }
      callBack?.(-1);
      return;
    }
    // 当前接口没有返回默认选中host_id
    if (!Number(selectId)) {
      selectId = res?.org_list?.[0]?.host_id;
    }
    if (selectId) {
      const detail = getHostRoleInList(selectId as string, res?.org_list);
      setSelectedHostId(selectId);
      updateAppCtxHostDetail(selectId, detail);
    }
    callBack?.(selectId);
  }, 500);

  return (
    <AppContext.Provider
      value={{
        statusInfo,
        vipResourceDetail,
        isMobile: mobile,
        isMobileUi,
        hostDetail,
        userData,
        isHost,
        registerHostData: regiHostData,
        userHostList,
        updateRegisterHostData,
        updateAppCtxHostDetail,
        setUserData: (user: any) => setUserData(user || {}),
        setSelectHostId,
        updateHostList: (callBack?: Function) => {
          updateHostList(callBack);
        },
      }}
    >
      {React.cloneElement(children, { isMobile: mobile, key: 'app-page' })}
    </AppContext.Provider>
  );
};

const ToastLoading = Toast.loading;

// 覆盖loading
Toast.loading = (msg, options) => {
  const icon = <ToastIcon />;
  return ToastLoading(msg, {
    icon,
    ...options,
  });
};

export default RouterBefore;
